import { zodResolver } from "@hookform/resolvers/zod";
import { useAtom } from "jotai";
import { useEffect } from "react";
import { useFieldArray, useForm, useWatch } from "react-hook-form";
import { expenseEntrySchema } from "web_myplann/Finances/Forms/expenseEntrySchema";
import { createTentativeExpense } from "web_myplann/Finances/Helpers/createTentativeExpense";
import { getCollapsedEntryId } from "web_myplann/Finances/Helpers/getCollapsedEntryId";
import { isDetailedExpenses } from "web_myplann/Finances/Helpers/isDetailedExpenses";
import { financeState } from "web_myplann/Finances/State/FinanceAtom";
import { z } from "zod";

export const getExpenseFormSchema = () =>
	z.object({
		expenses: z.array(expenseEntrySchema),
		totalValue: z.number().optional(),
		exact: z.boolean().optional(),
	});

export type ExpenseFormModel = z.infer<ReturnType<typeof getExpenseFormSchema>>;

export const useExpenseForm = () => {
	const [finance, setFinance] = useAtom(financeState);

	const isExact = isDetailedExpenses(finance?.expenses ?? []);

	const form = useForm<ExpenseFormModel>({
		resolver: zodResolver(getExpenseFormSchema()),
		values: {
			expenses: finance?.expenses ?? [],
			totalValue: finance?.expenses.reduce(
				(acc, expense) => acc + expense.monthlyPayment,
				0,
			),
			exact: isExact,
		},
	});

	const { setValue, control } = form;

	const {
		fields: expensesFields,
		append: expensesAppend,
		remove: expensesRemove,
	} = useFieldArray({
		control,
		name: "expenses",
	});

	const onSubmit = form.handleSubmit((data) => {
		if (!data.exact) {
			setFinance({
				...finance,
				expenses: [
					createTentativeExpense(
						getCollapsedEntryId(finance?.expenses ?? []),
						data.totalValue ?? 0,
					),
				],
			});
			return;
		}
		setFinance({
			...finance,
			expenses: data.expenses,
		});
	});

	const watchedExpenses = useWatch({ control, name: "expenses" });
	useEffect(() => {
		const sum =
			(watchedExpenses ?? []).reduce((acc, curr) => {
				const n = Number.parseFloat(
					String(curr?.monthlyPayment ?? "0").replace(",", "."),
				);
				return acc + (Number.isNaN(n) ? 0 : n);
			}, 0) ?? 0;
		setValue("totalValue", sum);
	}, [watchedExpenses, setValue]);

	return {
		onSubmit,
		form,
		setValue,
		control,
		expensesFields,
		expensesAppend,
		expensesRemove,
	};
};
